
import { supabase } from '@/integrations/supabase/client';
import { SupabaseUser, SupabaseCollection, SupabaseVideo } from '@/types/supabase';
import { TikTokVideo } from '@/types/api';
import { v4 as uuidv4 } from 'uuid';

// Max number of retries for database operations
const MAX_RETRIES = 3;

/**
 * Retry mechanism for database operations
 */
async function withRetry<T>(operation: () => Promise<T>, retries = MAX_RETRIES, delay = 500): Promise<T> {
  try {
    return await operation();
  } catch (error) {
    if (retries <= 0) throw error;
    console.log(`Operation failed, retrying... (${MAX_RETRIES - retries + 1}/${MAX_RETRIES})`);
    await new Promise(resolve => setTimeout(resolve, delay));
    return withRetry(operation, retries - 1, delay * 1.5);
  }
}

/**
 * Create or update a user in Supabase
 */
export async function createOrUpdateUser(
  email: string,
  firstName: string,
  lastName: string
): Promise<string> {
  try {
    console.log(`Starting createOrUpdateUser for: ${email}`);
    
    // Check if user exists
    const { data: existingUsers, error: queryError } = await withRetry(() => 
      supabase
        .from('users')
        .select('id')
        .eq('email', email)
        .limit(1)
    );

    if (queryError) {
      console.error("Error checking for existing user:", queryError);
      throw queryError;
    }

    if (existingUsers && existingUsers.length > 0) {
      // Update existing user's last_activity
      const userId = existingUsers[0].id;
      console.log(`Updating existing user: ${userId}`);
      
      const { error: updateError } = await withRetry(() => 
        supabase
          .from('users')
          .update({
            last_activity: new Date().toISOString(),
            first_name: firstName, // Update in case they changed it
            last_name: lastName,
          })
          .eq('id', userId)
      );
      
      if (updateError) {
        console.error("Error updating existing user:", updateError);
        throw updateError;
      }
      
      return userId;
    } else {
      // Create new user
      const userId = uuidv4();
      const now = new Date().toISOString();
      
      console.log(`Creating new user with ID: ${userId}`);
      const { error: insertError } = await withRetry(() => 
        supabase
          .from('users')
          .insert({
            id: userId,
            email,
            first_name: firstName,
            last_name: lastName,
            created_at: now,
            last_activity: now,
          })
      );

      if (insertError) {
        console.error("Error creating new user:", insertError);
        throw insertError;
      }
      
      return userId;
    }
  } catch (error) {
    console.error('Error creating/updating user in Supabase:', error);
    throw error;
  }
}

/**
 * Create a new collection record in Supabase
 */
export async function createCollection(
  userId: string,
  collectionUrl: string,
  videoCount: number,
  totalDuration: number = 0
): Promise<string> {
  try {
    console.log(`Creating collection for user ${userId} with ${videoCount} videos`);
    
    const collectionId = uuidv4();
    const now = new Date().toISOString();
    
    const { error } = await withRetry(() => 
      supabase
        .from('collections')
        .insert({
          id: collectionId,
          user_id: userId,
          collection_url: collectionUrl,
          title: null, // Will be updated later if available
          video_count: videoCount,
          total_duration: totalDuration,
          created_at: now,
          status: 'requested',
          email_sent: false,
          error_message: null,
        })
    );

    if (error) {
      console.error("Error creating collection:", error);
      throw error;
    }
    
    return collectionId;
  } catch (error) {
    console.error('Error creating collection in Supabase:', error);
    throw error;
  }
}

/**
 * Store videos from a TikTok collection in Supabase
 */
export async function storeVideos(
  collectionId: string,
  videos: TikTokVideo[]
): Promise<void> {
  try {
    console.log(`Storing ${videos.length} videos for collection ${collectionId}`);
    
    const now = new Date().toISOString();
    
    const formattedVideos = videos.map(video => ({
      id: uuidv4(),
      collection_id: collectionId,
      tiktok_id: video.id,
      description: video.description || '',
      author: typeof video.author === 'string' ? { name: video.author } : video.author,
      duration: video.duration,
      thumbnail: video.thumbnail,
      video_url: video.video_url,
      status: video.status || 'available',
      stats: video.stats || null,
      created_at: now,
    }));
    
    // Insert in batches to avoid payload size limits
    const batchSize = 50;
    for (let i = 0; i < formattedVideos.length; i += batchSize) {
      const batch = formattedVideos.slice(i, i + batchSize);
      
      console.log(`Inserting batch ${i/batchSize + 1} of ${Math.ceil(formattedVideos.length/batchSize)}`);
      
      await withRetry(async () => {
        const { error } = await supabase.from('videos').insert(batch);
        if (error) {
          console.error('Error inserting video batch to Supabase:', error);
          console.error('Error details:', error.details, error.hint, error.message);
          throw error;
        }
      });
    }
    
    // Update collection status to completed
    const { error: updateError } = await withRetry(() => 
      supabase
        .from('collections')
        .update({
          status: 'completed',
          video_count: videos.length,
        })
        .eq('id', collectionId)
    );
      
    if (updateError) {
      console.error('Error updating collection status:', updateError);
      throw updateError;
    }
    
  } catch (error) {
    console.error('Error storing videos in Supabase:', error);
    throw error;
  }
}

/**
 * Update collection email status
 */
export async function updateCollectionEmailStatus(
  collectionId: string, 
  emailSent: boolean,
  status: 'processing' | 'completed' | 'failed' = 'completed',
  errorMessage: string | null = null
): Promise<void> {
  try {
    console.log(`Updating collection ${collectionId} email status to ${emailSent ? 'sent' : 'not sent'}, status: ${status}`);
    
    const { error } = await withRetry(() => 
      supabase
        .from('collections')
        .update({
          email_sent: emailSent,
          status,
          error_message: errorMessage,
        })
        .eq('id', collectionId)
    );
      
    if (error) {
      console.error('Error updating collection email status:', error);
      throw error;
    }
    
  } catch (error) {
    console.error('Error updating collection email status:', error);
    throw error;
  }
}

/**
 * Log a download attempt (successful or failed)
 */
export async function logDownloadAttempt(
  userId: string,
  collectionUrl: string,
  status: 'success' | 'failed',
  errorMessage: string | null = null,
  email: string
): Promise<void> {
  try {
    console.log(`Logging download attempt for user ${userId}, status: ${status}`);
    
    const attemptId = uuidv4();
    const now = new Date().toISOString();
    
    const { error } = await withRetry(() => 
      supabase
        .from('download_attempts')
        .insert({
          id: attemptId,
          user_id: userId,
          collection_url: collectionUrl,
          status,
          error_message: errorMessage,
          created_at: now,
          email
        })
    );
      
    if (error) {
      console.error('Error logging download attempt:', error);
      throw error;
    }
    
  } catch (error) {
    console.error('Error logging download attempt:', error);
  }
}
